import { Component, Input, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { WaitActionDefinitionModel } from 'src/app/models/api/playbooks/actions/waitProperties';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import { PlaybooksActionDefinitionInput } from 'src/app/models/componentInputs/playbookActionInput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { WaitActionSupportedTimeUnitsOptions } from 'src/app/models/selectOptions/waitActionSupportedTimeUnitsOptions';
import {
  getSecondsForTimeUnit,
  getTimeUnitFromSeconds,
  getWaitActionSupportedTimeUnits,
  TimeUnits,
} from 'src/app/models/timeUnits';

@Component({
  selector: 'app-wait-action',
  templateUrl: './wait-action.component.html',
  styleUrls: ['./wait-action.component.scss'],
})
export class WaitActionComponent implements OnInit {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksActionInput: PlaybooksActionDefinitionInput;
  @Input() public triggerType: TriggerTypes;
  public timeUnitOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    WaitActionSupportedTimeUnitsOptions
  );

  private _waitActionDefinitionModel?: WaitActionDefinitionModel;

  ngOnInit(): void {
    let timeUnit = TimeUnits.Days;
    let amountTime = 3;
    let maxWaitTimeInSeconds = amountTime * getSecondsForTimeUnit(timeUnit);
    if (this.playbooksActionInput.actionDefinitionProperties) {
      this._waitActionDefinitionModel = this.playbooksActionInput
        .actionDefinitionProperties as WaitActionDefinitionModel;
      const seconds = this._waitActionDefinitionModel.maxWaitTimeInSeconds;
      timeUnit = getTimeUnitFromSeconds(seconds, getWaitActionSupportedTimeUnits());
      amountTime = seconds / getSecondsForTimeUnit(timeUnit);
      maxWaitTimeInSeconds = seconds;
    }

    const { min, max } = this._getMinMaxAwaitTime(timeUnit);

    this.fg.addControl(
      'waitAmount',
      new FormControl<number>(amountTime, [Validators.required, Validators.min(min), Validators.max(max)])
    );
    this.fg.addControl('waitUnitType', new FormControl<string>(timeUnit, [Validators.required]));
    this.fg.addControl('maxWaitTimeInSeconds', new FormControl<number>(maxWaitTimeInSeconds, [Validators.required]));

    this.fg.controls['waitAmount'].valueChanges.subscribe((newValue) => {
      if (this.fg.controls['waitAmount'].valid) {
        this.fg.controls['maxWaitTimeInSeconds'].setValue(
          newValue * getSecondsForTimeUnit(this.fg.controls['waitUnitType'].value)
        );
      }
    });

    this.fg.controls['waitUnitType'].valueChanges.subscribe((newValue) => {
      if (this.fg.controls['waitUnitType'].valid) {
        this.fg.controls['maxWaitTimeInSeconds'].setValue(
          this.fg.controls['waitAmount'].value * getSecondsForTimeUnit(newValue)
        );
      }
    });
  }

  public waitUnitTypeOnSelection(option?: ISelectOption) {
    const { min, max } = this._getMinMaxAwaitTime(option?.id as TimeUnits);
    const currentWaitAmount = this.fg.controls['waitAmount'].value;
    this.fg.controls['waitAmount'].setValidators([Validators.required, Validators.min(min), Validators.max(max)]);
    if (currentWaitAmount < min || currentWaitAmount > max) {
      this.fg.controls['waitAmount'].setValue(min);
    }
    this.fg.controls['waitAmount'].updateValueAndValidity();
    this.fg.controls['waitUnitType'].setValue(option?.id ?? '');
  }

  private _getMinMaxAwaitTime(timeUnit?: TimeUnits): {
    min: number;
    max: number;
  } {
    let min = 3;
    let max = 30;
    switch (timeUnit) {
      case TimeUnits.Weeks:
        min = 1;
        max = 4;
        break;
    }
    return { min, max };
  }
}
